import type { ExperimentConfig } from '@/types';

const experiment8Config: ExperimentConfig = {
  id: 'exp8',
  number: 8,
  name: 'Nitrite Content Determination',
  description: 'Determine nitrite content in water and soil using the Griess reaction (Marshall\'s Reagent) and Spectronic 20 spectrophotometry at 510 nm. Prepare calibration standards from 10 ppm stock and analyze unknown samples.',
  estimatedTime: '90 minutes',
  learningObjectives: [
    'Understand the Griess reaction (diazotization-coupling) for nitrite detection',
    'Apply spectrophotometric analysis at 510 nm using Spectronic 20 (pink-red azo dye)',
    'Prepare standards from 10 ppm NO₂⁻ stock solution (0.0, 0.5, 1.0, 1.5, 2.0, 2.5 ppm) in 100 mL volumetric flasks',
    'Add 4 mL Marshall\'s reagent to each standard/blank flask; add 1 mL to each unknown water sample',
    'Allow exactly 20 minutes for colour development (critical timing)',
    'Use the same Spectronic 20 cuvette for blank, all standards, and samples',
    'Condition cuvette with next standard after each reading',
    'Ensure all Spec 20 readings are < 2; if not, dilute further',
    'Construct calibration curves using linear regression (Beer-Lambert Law)',
    'Analyze unknown water samples for nitrite content using equation of the line',
  ],
  safetyNotes: [
    'Handle reagents with care - Marshall\'s reagent is an irritant',
    'Dispose of waste solutions properly in designated containers',
    'Avoid contact with skin and eyes',
    'NO₂⁻ solutions are toxic at high concentrations',
  ],
  requiredEquipment: [
    'Spectronic 20 spectrophotometer',
    'Spectronic 20 cuvette (same cuvette for all readings)',
    '100 mL volumetric flasks (5)',
    'Volumetric pipettes',
    '100 mL graduated cylinder',
    'Erlenmeyer flasks (labelled)',
    'Timer (20 minutes)',
    'Wash bottle',
    '10 ppm NO₂⁻ stock solution',
    'Marshall\'s reagent (Griess reagent) — 4 mL per standard/blank, 1 mL per unknown',
    'Deionised water',
  ],
  parts: [
    {
      id: 0,
      name: 'summary',
      title: 'Summary',
      steps: [],
      equipment: [],
      initialState: {},
    },
    {
      id: 1,
      name: 'preparation',
      title: 'Part I: Standard and Sample Preparation',
      equipment: [
        'Volumetric flasks (100 mL)',
        'Volumetric pipettes',
        'Timer',
        'Wash bottle'
      ],
      initialState: {
        standards: [
          { concentration: 0.0, volume: 0, prepared: false, color: '#FFFFFF' },
          { concentration: 0.5, volume: 5.0, prepared: false, color: '#FFFFFF' },
          { concentration: 1.0, volume: 10.0, prepared: false, color: '#FFFFFF' },
          { concentration: 1.5, volume: 15.0, prepared: false, color: '#FFFFFF' },
          { concentration: 2.0, volume: 20.0, prepared: false, color: '#FFFFFF' },
          { concentration: 2.5, volume: 25.0, prepared: false, color: '#FFFFFF' }
        ],
        unknown: {
          prepared: false,
          color: '#FFFFFF'
        },
        timer: {
          isRunning: false,
          secondsRemaining: 1200,
          colorDevelopmentProgress: 0
        },
        reagentAdded: false
      },
      steps: [
        {
          id: 1,
          title: 'Prepare Blank Solution',
          shortDescription: 'Prepare blank (0.0 ppm) using 100 mL deionised water (use graduated cylinder)',
          handler: 'handlePrepareBlank',
          infoContent: {
            title: 'Why a Blank?',
            content: 'The blank contains all reagents except nitrite and is used to zero the spectrophotometer. This corrects for any absorbance from the reagents themselves.',
            whatHappens: 'A blank solution of 100 mL deionised water (with Marshall\'s reagent added later) is prepared to serve as the zero-absorbance reference for the spectrophotometer.',
            observe: [
              'Measure 100 mL deionised water using a graduated cylinder into a volumetric flask',
              'The blank should remain colourless after reagent addition (no nitrite = no colour)',
              'This solution corrects for any background absorbance from the reagents',
            ],
          }
        },
        {
          id: 2,
          title: 'Prepare Standard Solutions',
          shortDescription: 'Prepare calibration standards (0.5, 1.0, 1.5, 2.0, 2.5 ppm NO2-)',
          handler: 'handlePrepareStandards',
          infoContent: {
            title: 'Standard Preparation from 10 ppm Stock',
            content: `Standards are prepared by diluting a **10 ppm NO₂⁻** stock solution into **100 mL** volumetric flasks and diluting to the mark with **deionised water**. Pour into properly labelled Erlenmeyer flasks.

| Standard | Stock Volume (10 ppm) | Final Conc. |
|----------|----------------------|-------------|
| 0.5 ppm  | 5.0 mL               | 0.5 ppm     |
| 1.0 ppm  | 10.0 mL              | 1.0 ppm     |
| 1.5 ppm  | 15.0 mL              | 1.5 ppm     |
| 2.0 ppm  | 20.0 mL              | 2.0 ppm     |
| 2.5 ppm  | 25.0 mL              | 2.5 ppm     |

Arrive at the lab with dilution calculations complete.`,
            whatHappens: 'Known volumes of the 10 ppm NO2- stock are pipetted into 100 mL volumetric flasks and diluted to the mark, creating a series of standards spanning 0.5 to 2.5 ppm.',
            observe: [
              'Pipette the exact volumes listed for each standard concentration',
              'Dilute each to the 100 mL mark with deionised water and mix thoroughly',
              'Transfer to labelled Erlenmeyer flasks — all standards appear colourless at this stage',
            ],
          }
        },
        {
          id: 3,
          title: 'Prepare Unknown Sample',
          shortDescription: 'Each student takes two 25 mL portions of the unknown water sample for individual analysis',
          handler: 'handlePrepareUnknown'
        },
        {
          id: 4,
          title: 'Add Marshall\'s Reagent',
          shortDescription: 'Pipet 4 mL Marshall\'s reagent into each standard/blank flask; pipet 1 mL into each unknown water sample',
          handler: 'handleAddReagent',
          infoContent: {
            title: 'The Griess Reaction (Marshall\'s Reagent)',
            content: `Pipet **4 mL** of Marshall\'s Reagent into each of the Erlenmeyer flasks containing your nitrite standards as well as the blank. Pipet **1 mL** of Marshall\'s Reagent into each unknown water sample. Swirl the flasks to mix.

Discovered in 1858 by Peter Griess, this reaction is the standard method for nitrite detection. Nitrite reacts with sulfanilamide to form a diazonium compound, which then couples with NED to form a pink-red azo dye.

**Colour progression:** Blank stays white/clear → Standards develop **pink to red** colour proportional to concentration.`,
            chemicalEquation: 'NO₂⁻ + Sulfanilamide + NED → Pink-red azo dye (λmax = 510 nm)',
            whatHappens: 'Marshall\'s reagent (Griess reagent) reacts with nitrite via diazotization and coupling to form a pink-red azo dye whose intensity is proportional to nitrite concentration.',
            observe: [
              'Add 4 mL Marshall\'s reagent to each standard/blank flask; 1 mL to each unknown',
              'Swirl to mix — colour begins developing immediately but takes 20 minutes for completion',
              'Higher nitrite concentrations produce a more intense pink-red colour',
            ],
          }
        },
        {
          id: 5,
          title: 'Mix and Start Timer',
          shortDescription: 'Mix thoroughly and start the 20-minute color development timer',
          handler: 'handleStartTimer',
          validation: {
            requires: ['reagentAdded']
          },
          infoContent: {
            title: 'Why 20 Minutes?',
            content: 'Color development takes time because the coupling reaction is not instantaneous. Measuring too early gives low results. The full color develops in exactly **20 minutes** and remains **stable for several hours** after development is complete.',
            whatHappens: 'The azo coupling reaction requires exactly 20 minutes for the colour to fully develop; the resulting pink-red dye is stable for several hours after completion.',
            observe: [
              'Start the 20-minute timer immediately after mixing',
              'Colour gradually intensifies from colourless to pink/red over the 20 minutes',
              'Do not measure absorbance before the full 20 minutes have elapsed',
            ],
          }
        },
        {
          id: 6,
          title: 'Observe Color Development',
          shortDescription: 'Watch as the pink color develops over time',
          handler: 'handleObserveColor'
        }
      ]
    },
    {
      id: 2,
      name: 'measurement',
      title: 'Part II: Spectrophotometric Measurement',
      equipment: [
        'Spectronic 20 spectrophotometer',
        'Spectronic 20 cuvette (use same cuvette for all readings)',
      ],
      initialState: {
        spectrophotometer: {
          isOn: true,
          wavelength: 510,
          isZeroed: false,
          currentReading: null
        },
        cuvettes: {
          blank: { filled: false, absorbance: null },
          std05: { filled: false, absorbance: null },
          std10: { filled: false, absorbance: null },
          std15: { filled: false, absorbance: null },
          std20: { filled: false, absorbance: null },
          std25: { filled: false, absorbance: null },
          unknown: { filled: false, absorbance: null }
        },
        calibration: {
          points: [],
          slope: null,
          intercept: null,
          r2: null
        },
        result: {
          unknownConcentration: null
        }
      },
      steps: [
        {
          id: 1,
          title: 'Wait for Timer',
          shortDescription: 'Wait until the 20-minute color development is complete',
          handler: 'handleWaitForTimer',
          validation: {
            condition: 'timer.secondsRemaining === 0'
          }
        },
        {
          id: 2,
          title: 'Transfer to Cuvettes',
          shortDescription: 'Obtain a Spectronic 20 cuvette and wash if necessary. Use the same cuvette for all readings.',
          handler: 'handleFillCuvettes',
          infoContent: {
            title: 'Cuvette Handling',
            content: 'Use the **same cuvette** for the blank, all standards, and samples. Take readings from **lowest to highest concentration**, conditioning the cuvette with the next standard after each reading, discarding the conditionings and refilling with standard to be measured. Rinse the cuvette thoroughly before reading the unknown samples but do not forget to condition. Ensure all readings on the Spec 20 are **< 2**; if not, dilute them further.',
            whatHappens: 'The same cuvette is used throughout for consistency; reading from lowest to highest concentration and conditioning between readings prevents carryover contamination.',
            observe: [
              'Use the same cuvette for all measurements to eliminate cuvette-to-cuvette variation',
              'Condition the cuvette by rinsing with the next solution before measuring it',
              'Read from lowest concentration to highest to minimize contamination',
            ],
          }
        },
        {
          id: 3,
          title: 'Set Wavelength',
          shortDescription: 'Set the spectrophotometer wavelength to 510 nm',
          handler: 'handleSetWavelength',
          infoContent: {
            title: 'Why 510 nm?',
            content: 'The azo dye absorbs green light (around 510 nm) most strongly, which is why it appears pink-red (the complementary color). Measuring at λmax gives maximum sensitivity.',
            whatHappens: 'The spectrophotometer is set to 510 nm, the wavelength of maximum absorption for the pink-red azo dye, to achieve the greatest sensitivity for nitrite detection.',
            observe: [
              'Set the wavelength dial precisely to 510 nm',
              'The azo dye appears pink-red because it absorbs green light (510 nm) and transmits red',
              'Maximum sensitivity occurs at the absorption maximum wavelength',
            ],
          }
        },
        {
          id: 4,
          title: 'Zero with Blank',
          shortDescription: 'Use blank solution as reference to set Absorbance reading to zero',
          handler: 'handleZeroSpec',
          validation: {
            requires: ['cuvettes.blank.filled']
          }
        },
        {
          id: 5,
          title: 'Measure Standards',
          shortDescription: 'Measure absorbance of each standard solution',
          handler: 'handleMeasureStandards',
          validation: {
            requires: ['spectrophotometer.isZeroed']
          }
        },
        {
          id: 6,
          title: 'Plot Calibration Curve',
          shortDescription: 'Plot absorbance vs concentration. Include line of best fit and equation of the line.',
          handler: 'handlePlotCalibration',
          infoContent: {
            title: 'Calibration Curve',
            content: 'Using Excel, plot absorbance versus concentration on the horizontal scale for each standard. Include a **line of best fit** through these points and the point at zero concentration. Plot a rough graph in your lab notebook to ensure it is linear. The absorbance readings of the unknown nitrite samples are **not** used to create the standard curve.',
            whatHappens: 'A calibration curve (absorbance vs. concentration) is constructed from the standard measurements; the linear relationship obeys Beer-Lambert Law and allows determination of unknown concentrations.',
            observe: [
              'Plot absorbance (y-axis) versus concentration in ppm (x-axis)',
              'The points should form a straight line passing through or near the origin',
              'Include the line of best fit equation and R-squared value',
            ],
          }
        },
        {
          id: 7,
          title: 'Measure Unknown',
          shortDescription: 'Measure the absorbance of the unknown sample',
          handler: 'handleMeasureUnknown'
        },
        {
          id: 8,
          title: 'Calculate Concentration',
          shortDescription: 'Using the equation of the line, solve for the unknown concentration',
          handler: 'handleCalculateConcentration',
          validation: {
            requires: ['calibration.slope', 'cuvettes.unknown.absorbance']
          },
          infoContent: {
            title: 'Environmental Significance',
            content: 'Using the equation of the line, solve for the unknown concentration. Nitrite in drinking water is a health concern, especially for infants (cyanosis / "blue baby syndrome"). Nitrites reduce the blood\'s ability to transport oxygen. Nitrite can come from fertilizer runoff, soil bacteria, or food additives (cured meats). Ensure values fall within the standard curve range.',
            whatHappens: 'The unknown sample absorbance is substituted into the calibration equation to determine the nitrite concentration in the water sample.',
            observe: [
              'Substitute the unknown absorbance into the equation of the line to solve for concentration',
              'Verify that the unknown absorbance falls within the range of the standard curve',
              'If absorbance exceeds the highest standard, dilute the sample and re-measure',
            ],
          }
        }
      ]
    }
  ],
  quiz: {
    experimentId: 'exp8',
    title: 'Nitrite Content Determination Quiz',
    questions: [
      {
        id: 'exp8-q1',
        type: 'mcq' as const,
        question: 'What is the Griess reaction used for in this experiment?',
        options: [
          { id: 'a', text: 'Detecting chloride ions' },
          { id: 'b', text: 'Detecting nitrite (NO₂⁻) through formation of a pink-red azo dye' },
          { id: 'c', text: 'Detecting iron ions' },
          { id: 'd', text: 'Detecting phosphate ions' },
        ],
        correctOptionId: 'b',
        explanation: 'The Griess reaction (diazotization-coupling) specifically detects nitrite by forming a pink-red azo dye that can be measured spectrophotometrically.',
      },
      {
        id: 'exp8-q2',
        type: 'mcq' as const,
        question: 'Why must you wait exactly 20 minutes after adding Marshall\'s reagent?',
        options: [
          { id: 'a', text: 'For the solution to cool down' },
          { id: 'b', text: 'For complete colour development of the azo dye' },
          { id: 'c', text: 'For the precipitate to settle' },
          { id: 'd', text: 'For the reagent to dissolve' },
        ],
        correctOptionId: 'b',
        explanation: 'The diazotization-coupling reaction requires exactly 20 minutes for complete colour development. Measuring too early or too late gives inaccurate results.',
      },
      {
        id: 'exp8-q3',
        type: 'calculation' as const,
        question: 'To prepare a 1.5 ppm NO₂⁻ standard in 100 mL from a 10 ppm stock, how many mL of stock solution are needed?',
        expectedValue: 15,
        tolerance: 3,
        unit: 'mL',
        explanation: 'C₁V₁ = C₂V₂: 10 ppm × V₁ = 1.5 ppm × 100 mL, so V₁ = 15 mL',
      },
      {
        id: 'exp8-q4',
        type: 'short_answer' as const,
        question: 'Why must the same cuvette be used for all Spectronic 20 readings, and why should it be conditioned with the next standard?',
        sampleAnswer: 'Using the same cuvette eliminates variability from differences in cuvette thickness, scratches, or optical properties. Conditioning (rinsing) with the next standard before reading removes residual previous solution, preventing cross-contamination and ensuring accurate absorbance measurements.',
      },
      {
        id: 'exp8-q5',
        type: 'mcq' as const,
        question: 'What should you do if a Spec 20 reading exceeds 2.0?',
        options: [
          { id: 'a', text: 'Record it anyway' },
          { id: 'b', text: 'Recalibrate the instrument' },
          { id: 'c', text: 'Dilute the sample further and re-measure' },
          { id: 'd', text: 'Use a different wavelength' },
        ],
        correctOptionId: 'c',
        explanation: 'Absorbance readings above 2.0 are unreliable because less than 1% of light is transmitted. The sample must be diluted and re-measured within the linear range.',
      },
    ],
  },
};

export default experiment8Config;
